"use client";

import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ClientVoices() {
  const testimonials = [{
    quote: "Vivid Studio transformed our brand narrative into a cinematic experience that truly resonates with our audience.",
    author: "Sarah Jenkins",
    position: "CMO",
    location: "TechFlow"
  }, {
    quote: "The level of detail in their creative direction is unmatched. They don't just make ads; they build worlds.",
    author: "Marcus Thorne",
    position: "Brand Director",
    location: "Lumina"
  }, {
    quote: "Working with the team was a masterclass in storytelling. Our campaign engagement metrics have never been higher.",
    author: "Elena Rodriguez",
    position: "Head of Marketing",
    location: "Veloce"
  }, {
    quote: "Their ability to scale social-first content without losing the cinematic quality is exactly what we needed.",
    author: "David Chen",
    position: "Creative Lead",
    location: "Nexus"
  }, {
    quote: "Vivid Studio is the gold standard for production. Every frame feels intentional, bold, and perfectly executed.",
    author: "Amara Okafor",
    position: "Founder",
    location: "Aura"
  }];
  const [activeIndex, setActiveIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const goTo = (index: number) => {
    if (index === activeIndex) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setActiveIndex(index);
      setIsTransitioning(false);
    }, 400);
  };
  const handlePrev = () => {
    const next = activeIndex === 0 ? testimonials.length - 1 : activeIndex - 1;
    goTo(next);
  };
  const handleNext = () => {
    const next = activeIndex === testimonials.length - 1 ? 0 : activeIndex + 1;
    goTo(next);
  };
  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setActiveIndex(prev => prev === testimonials.length - 1 ? 0 : prev + 1);
        setIsTransitioning(false);
      }, 400);
    }, 7000);
    return () => clearInterval(interval);
  }, [testimonials.length]);
  const current = testimonials[activeIndex];
  return <section data-section-id="4407" id="client-voices" className="bg-background text-foreground relative overflow-hidden py-32 md:py-40 px-4">
      {}
      <div className="pointer-events-none absolute inset-0 bg-center bg-no-repeat bg-contain opacity-[0.05] blur-sm bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/vivid_studio_grid.webp)]" aria-hidden="true" />

      <div ref={ref} className={`relative max-w-6xl mx-auto transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        {}
        <div className="mb-20 md:mb-24">
          <span className="font-mono text-xs tracking-[0.3em] uppercase text-primary">CLIENT IMPACT</span>
        </div>

        {}
        <div className="relative flex items-center justify-center">
          {}
          <Button variant="ghost" size="icon" onClick={handlePrev} aria-label="Previous testimonial" className="absolute left-0 md:left-4 top-1/2 -translate-y-1/2 z-10 h-10 w-10 hover:bg-transparent text-foreground/60 hover:text-primary transition-colors">
            <ChevronLeft className="h-6 w-6" strokeWidth={1} />
          </Button>

          {}
          <div className={`text-center max-w-[900px] mx-auto px-12 md:px-20 transition-opacity duration-[800ms] ease-in-out ${isTransitioning ? "opacity-0" : "opacity-100"}`}>
            {}
            <div className="flex justify-center mb-10">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-primary" aria-hidden="true">
                <path d="M16 2L18 14L30 16L18 18L16 30L14 18L2 16L14 14L16 2Z" stroke="currentColor" strokeWidth="0.75" fill="none" />
                <circle cx="16" cy="16" r="3" stroke="currentColor" strokeWidth="0.75" fill="none" />
              </svg>
            </div>

            {}
            <blockquote className="font-default italic font-light text-foreground leading-[1.25]" style={{
            fontSize: "clamp(1.75rem, 4vw, 3rem)"
          }}>
              &ldquo;{current.quote}&rdquo;
            </blockquote>

            {}
            <div className="flex justify-center my-10">
              <div className="h-px w-16 bg-primary" />
            </div>

            {}
            <div className="font-mono text-[0.7rem] md:text-xs tracking-[0.25em] uppercase text-muted-foreground leading-relaxed">
              {current.author}
              <span className="mx-2 text-primary/60">/</span>
              {current.position}
              <span className="mx-2 text-primary/60">/</span>
              {current.location}
            </div>
          </div>

          {}
          <Button variant="ghost" size="icon" onClick={handleNext} aria-label="Next testimonial" className="absolute right-0 md:right-4 top-1/2 -translate-y-1/2 z-10 h-10 w-10 hover:bg-transparent text-foreground/60 hover:text-primary transition-colors">
            <ChevronRight className="h-6 w-6" strokeWidth={1} />
          </Button>
        </div>

        {}
        <div className="flex justify-center items-center gap-4 mt-20 md:mt-24">
          {testimonials.map((_, i) => <Button key={i} data-index={i} onClick={() => goTo(i)} aria-label={`Go to testimonial ${i + 1}`} variant="ghost" className={`h-1.5 px-0 transition-all duration-500 ${i === activeIndex ? "w-8 bg-primary hover:bg-primary" : "w-1.5 bg-primary/30 hover:bg-primary/60"}`} />)}
        </div>
      </div>
    </section>;
}